<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div id="home-page" @click.capture="onHomePageClick">
    <template v-if="showDevLinksComponent">
      <component :is="DevLinksComponent" :env-props="envProps" />
    </template>
    <template v-if="isError404">
      <component :is="Error404Component" />
    </template>
    <template v-else-if="isError500">
      <component :is="Error500Component" />
    </template>
    <div v-else-if="canvasData" id="canvas">
      <Canvas :canvas="canvasData">
        <template v-if="isZorShown" #zone-of-relevance>
          <TabbedZoneOfRelevance v-if="isZoRWithTabsVisible" :zor-tabs="zorTabs" />
          <ZoneOfRelevance v-else :zor-cards="zorCards" />
        </template>
        <template v-if="rviItems" #recently-viewed-items>
          <div id="recently-viewed-items" class="grid-container">
            <LazyLoadWhenInViewport v-if="isRVIEnabled">
              <component
                :is="RVIComponent"
                :pros-product-pool-media="{
                  componentType: 'RviCarousel',
                  panelType: 'rvi',
                  context: 'rvi_hpdash_zone',
                }"
                :properties="canvasData?.meta"
              />
            </LazyLoadWhenInViewport>
          </div>
        </template>
      </Canvas>
    </div>
  </div>
  <BackToTop />
  <PopUP ref="popUpRef" :mediacontent="mediacontent" />
</template>

<script setup>
import { onMounted, computed, defineAsyncComponent, ref, shallowRef, provide } from 'vue';
import CacheManager from '@vue-util/cacheManager';
import Canvas from '@nuxt-common/canvas-aem/app/components/CanvasMain';
import BackToTop from '@atomic-ui/backToTop/BackToTop';
import useGoogleAdsManager from '@nuxt-common/site-monetization/app/composables/useGoogleAdsManager';
import logger from '@vue-util/logger';
import ZoneOfRelevance from '@/components/ZoneOfRelevance/ZoneOfRelevance';
import TabbedZoneOfRelevance from '@/components/ZoneOfRelevance/TabbedZoneOfRelevance';
import { ZOR_TAB_EXPERIMENT_TREATMENT_ID } from '@/components/ZoneOfRelevance/constants';
import { SET_ZOR_INFO, SET_RVI_IDS } from '@/types/mutations';
import { IS_ZONE_OF_RELEVANCE_ENABLED, GET_ZOR_CARDS, GET_ZOR_TABS } from '@/types/getters';
import { FETCH_ZOR_CARDS, FETCH_ZOR_TABS } from '@/types/actions';
import loadIshipWelcomePopup from '@/utils/loadIshipContent';
import handlePopUpLink from '@/utils/handlePopUpLink';
import LazyLoadWhenInViewport from '@/components/modules/LazyLoadWhenInViewport';
import useRoktSDKPlus from '@nuxt-common/site-monetization/app/composables/useRoktSDKPlus';

import { useEnvProps } from '@/stores/envProps';
import { usePageData } from '@/stores/pageData';

import { useZoneOfRelevance } from '@/stores/zoneOfRelevance/zoneOfRelevance';

const PopUP = defineAsyncComponent(() => import('@nuxt-common/canvas-aem/app/components/popUp/PopUp'));
const envPropsStore = useEnvProps();
const pageDataStore = usePageData();
const envProps = computed(() => envPropsStore?.envProps);
const canvasData = computed(() => pageDataStore?.pageData?.canvas);
const isZoRWithTabsVisible = computed(() => canvasData?.value?.meta?.context?.customerExperiment?.includes(ZOR_TAB_EXPERIMENT_TREATMENT_ID));

provide('experimentIds', canvasData?.value?.meta?.context?.customerExperiment || '');

const tagEnv = envProps?.value?.tagMngrEnv;
const brand = envProps?.value?.BRAND;

const zoneOfRelevanceStore = useZoneOfRelevance();
const isZoneOfRelevanceEnabled = computed(() => zoneOfRelevanceStore[IS_ZONE_OF_RELEVANCE_ENABLED]);

// /dev/dev-links route only
const devLinksEnabled = computed(() => envProps.value?.showDevLinks);
const DevLinksComponent = shallowRef(null);

const isCSR = ref(false);
const showDevLinksComponent = computed(() => devLinksEnabled.value && isCSR.value && !!DevLinksComponent.value);

const popUpRef = ref(null);
const mediacontent = ref({});

// will only work on origin/staging when signed into vpn, or on dev

if (devLinksEnabled.value) {
  const loadDevLinks = async () => {
    try {
      const module = await import('@/composables/useDevLinks');
      const devLinks = module.useDevLinks();
      DevLinksComponent.value = devLinks.devLinksComponent.value;
    } catch (error) {
      logger.error('Failed to load dev tools:', error);
    }
  };
  onMounted(loadDevLinks);
}

// Remove the filter if Loyalty Card needs to be added to ZOR again
const zorCards = computed(() => {
  const filteredCards = zoneOfRelevanceStore[GET_ZOR_CARDS].filter((item) => item?.cardType?.toUpperCase() !== 'LOYALTY');
  return brand === 'bcom' && filteredCards.length > 6 ? filteredCards.slice(0, 6) : filteredCards;
});

// Zone of relevance tabs
const zorTabs = computed(() => (isZoRWithTabsVisible.value ? zoneOfRelevanceStore[GET_ZOR_TABS] : []));

const isError404 = computed(() => {
  const errorExists = canvasData.value?.errors?.error || canvasData.value?.error;
  return errorExists && canvasData.value?.status >= 400 && canvasData.value?.status < 500;
});

const isError500 = computed(() => {
  const errorExists = canvasData.value?.errors?.error || canvasData.value?.error;
  return errorExists && canvasData.value?.status >= 500;
});

/* if redirection is enabled */
const isRedirectEnabled = computed(() => canvasData.value?.redirectEnabled);
if (isRedirectEnabled?.value) {
  window.location.replace(isRedirectEnabled.value);
}

/* zone of relevance */
if (isError404.value || isError500.value) {
  zoneOfRelevanceStore[SET_ZOR_INFO]({});
}
if (canvasData?.value?.meta?.properties?.enableZoneOfRelevance) {
  zoneOfRelevanceStore[SET_ZOR_INFO](canvasData.value);
}

const Error404Component = defineAsyncComponent(() =>
  brand === 'mcom' ? import('@/components/mcom/Error404') : import('@/components/bcom/Error404'),
);

const Error500Component = defineAsyncComponent(() =>
  brand === 'mcom' ? import('@/components/mcom/Error500') : import('@/components/bcom/Error500'),
);

const RVIComponent = defineAsyncComponent(() => import('@nuxt-common/canvas-aem/app/components/pool/ProsProductPool'));

const isZorShown = computed(() => {
  if (!isZoneOfRelevanceEnabled.value) return false;
  const zorItems = isZoRWithTabsVisible.value ? zorTabs.value : zorCards.value;
  return zorItems.length >= 5;
});

const rviItems = computed(() => canvasData?.value?.meta?.properties?.homepageRecentlyViewedEnabled);

const isRVIEnabled = computed(() => {
  if (!isCSR.value) return false;

  const recentlyViewedItems = CacheManager.getPersistent('recentlyViewedItems');
  return Boolean(rviItems.value && recentlyViewedItems && (!isZorShown.value || !isZoneOfRelevanceEnabled.value));
});

const toggle = (id) => {
  popUpRef.value.toggleModal(id);
};

const onHomePageClick = (e) => {
  const link = e.target.closest('a[href*="popupOverlayId"], area[href*="popupOverlayId"]');
  if (!link) return;
  const popUpLinkHandler = handlePopUpLink(mediacontent, toggle);
  popUpLinkHandler({
    preventDefault: () => e.preventDefault(),
    target: {
      href: link.getAttribute('href'),
      text: link.text,
      textContent: link.textContent,
      setAttribute: link.setAttribute.bind(link),
    },
  });
};

onMounted(() => {
  isCSR.value = true;
});

onMounted(async () => {
  const enableGAMAdsFromApp = canvasData?.value?.meta?.properties?.enableGAMAdsFromApp || false;
  const enableGAMImpressionTags = canvasData?.value?.meta?.properties?.enableGAMImpressionTags || false;
  const enableGAMClickTags = canvasData?.value?.meta?.properties?.enableGAMClickTags || false;
  const isRoktSDKPlusEnabled = canvasData?.value?.meta?.properties?.isRoktSDKPlusEnabled || false;
  const isGoogleAdPromoSlideshowEnabled = canvasData?.value?.meta?.properties?.isGoogleAdPromoSlideshowEnabled || false;
  const deviceType = canvasData?.value?.meta?.context?.deviceType !== 'PHONE' ? 'desktop' : 'mobile';
  useGoogleAdsManager.initGAMTags({
    page: 'homepage',
    device: deviceType,
    enableGAMAdsFromApp,
    enableGAMImpressionTags,
    enableGAMClickTags,
    tagEnv,
    brand,
    isGoogleAdPromoSlideshowEnabled,
  });
  useRoktSDKPlus({
    brand,
    isRoktSDKPlusEnabled,
    screenName: 'home',
  });

  loadIshipWelcomePopup(canvasData?.value?.meta?.context, brand);

  if (isZoneOfRelevanceEnabled.value) {
    const rviIds = (localStorage.getItem('recentlyViewedItems') || '').split(',').filter(Boolean);
    zoneOfRelevanceStore[SET_RVI_IDS](rviIds);

    if (isZoRWithTabsVisible.value) {
      await zoneOfRelevanceStore[FETCH_ZOR_TABS]();
    } else {
      await zoneOfRelevanceStore[FETCH_ZOR_CARDS]();
    }
  }
});
</script>

<style lang="scss">
.home {
  position: inherit;
}

.product-thumbnail-container .toast {
  position: absolute;
}

#recently-viewed-items:not(:empty) {
  padding: rem-calc(0 16px 64px);

  @include breakpoint(medium down) {
    padding: rem-calc(0 16px 48px);
  }

  .pros-collection {
    overflow: visible;
  }
}
</style>
